import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatIconButtonSizesModule } from '@libs/shared/styles/mat-icon-button/mat-icon-button-sizes.module';

@Component({
  selector: 'app-chip',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatIconButtonSizesModule],
  template: `
    <div class="chip-container">
      <div class="chip-text flex flex-row items-center">
        @if (prefixIcon) {
          <mat-icon
            class="icon-size-3.5 mr-1 text-inherit"
            [svgIcon]="prefixIcon" />
        }
        <ng-content />
        @if (showDeleteButton) {
          <button mat-icon-button mat-tiny-icon-button (click)="onDelete()">
            <mat-icon [svgIcon]="'heroicons_outline:x-mark'" />
          </button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      app-chip {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 auto;
        padding: 0.25rem 0.5rem;
        font-size: 1rem;
        line-height: 1.5rem;
        font-weight: 500;
        border-width: 1px;
        cursor: pointer;

        .chip-container {
          .chip-text {
            @apply max-w-full flex-initial p-0.5 font-normal leading-none;
          }
        }

        &.chip-primary {
          @apply border-primary-300 bg-primary-100 text-primary-700 dark:bg-primary-700 dark:text-primary-300 dark:bg-opacity-25;

          &.selected {
            @apply border-primary-700 bg-primary-700 text-primary-100 dark:bg-primary-500 dark:text-primary-50;
          }
        }

        &.chip-gray {
          @apply border-gray-300 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:bg-opacity-25 dark:text-gray-300;

          &.selected {
            @apply border-gray-700 bg-gray-700 text-gray-100 dark:bg-gray-500 dark:text-gray-50;
          }
        }

        &.chip-green {
          @apply border-green-300 bg-green-100 text-green-700 dark:bg-green-700 dark:bg-opacity-25 dark:text-green-300;

          &.selected {
            @apply border-green-700 bg-green-700 text-green-100 dark:bg-green-500 dark:text-green-50;
          }
        }

        &.chip-purple {
          @apply border-purple-300 bg-purple-100 text-purple-700 dark:bg-purple-700 dark:bg-opacity-25 dark:text-purple-300;

          &.selected {
            @apply border-purple-700 bg-purple-700 text-purple-100 dark:bg-purple-500 dark:text-purple-50;
          }
        }

        &.chip-red {
          @apply border-red-300 bg-red-100 text-red-700 dark:bg-red-700 dark:bg-opacity-25 dark:text-red-300;

          &.selected {
            @apply border-red-700 bg-red-700 text-red-100 dark:bg-red-500 dark:text-red-50;
          }
        }

        &.chip-cyan {
          @apply border-cyan-300 bg-cyan-100 text-cyan-700 dark:bg-cyan-700 dark:bg-opacity-25 dark:text-cyan-300;

          &.selected {
            @apply border-cyan-700 bg-cyan-700 text-cyan-100 dark:bg-cyan-500 dark:text-cyan-50;
          }
        }

        &.chip-orange {
          @apply border-orange-300 bg-orange-100 text-orange-700 dark:bg-orange-700 dark:bg-opacity-25 dark:text-orange-300;

          &.selected {
            @apply border-orange-700 bg-orange-700 text-orange-100 dark:bg-orange-500 dark:text-orange-50;
          }
        }

        &.chip-small {
          @apply rounded-xl p-0.5 text-xs;
        }

        &.chip-medium {
          @apply rounded-2xl p-1 text-sm;
        }

        &.chip-large {
          @apply rounded-3xl p-2.5 text-lg;
        }
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ChipComponent {
  @Input() color:
    | 'primary'
    | 'gray'
    | 'green'
    | 'purple'
    | 'red'
    | 'cyan'
    | 'orange' = 'gray';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() selected = false;
  @Input() showDeleteButton = false;
  @Input() prefixIcon: string;
  @Output() byDelete = new EventEmitter<void>();

  @HostBinding('class') get classList(): any {
    return {
      'chip-small': this.size === 'sm',
      'chip-medium': this.size === 'md',
      'chip-large': this.size === 'lg',
      'chip-primary': this.color === 'primary',
      'chip-gray': this.color === 'gray',
      'chip-green': this.color === 'green',
      'chip-purple': this.color === 'purple',
      'chip-red': this.color === 'red',
      'chip-cyan': this.color === 'cyan',
      'chip-orange': this.color === 'orange',
      selected: this.selected,
    };
  }

  constructor() {}

  onDelete() {
    if (this.showDeleteButton) {
      this.byDelete.emit();
    }
  }
}
